import { CenteredContainer } from 'common-front/src/components/centeredContainer/centeredContainer';
import { Form } from 'common-front/src/components/form/form';
import { FormErrors } from 'common-front/src/components/form/formErrors';
import { FullScreenPopup } from 'common-front/src/components/fullScreenPopup/fullScreenPopup';
import { Button } from 'common-front/src/designSystem/components/button';
import { FormBox } from 'common-front/src/designSystem/components/formBox';
import { TextInput as TextInputComponent } from 'common-front/src/designSystem/components/textInput';
import { Content } from 'common-front/src/designSystem/components/tooltip/content';
import { Tooltip } from 'common-front/src/designSystem/components/tooltip/tooltip';
import { Trigger } from 'common-front/src/designSystem/components/tooltip/trigger';
import { RadioText } from 'common-front/src/designSystem/form/radio';
import { TextInput } from 'common-front/src/designSystem/form/textInput';
import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { useUniqueIds } from 'common-front/src/hooks/useUniqueIds';
import { LocalStorageKeys } from 'common-front/src/util/localStorage';
import { Box } from 'common/src/designSystem/components/box';
import { Flex } from 'common/src/designSystem/components/flex';
import { Spacer } from 'common/src/designSystem/components/spacer';
import {
    CustomFieldImportFragment,
    ImportMode,
    MappableImportInput,
    OrganizationId
} from 'common/src/generated/types';
import { CsvSettings } from 'common/src/input/csvInput';
import { MappableImportInputService } from 'common/src/input/mappableImportInput';
import { TranslationService } from 'common/src/services/translationService';
import { ValidateService } from 'common/src/services/validateService';
import { useLocalStorage, useService } from 'common/src/util/dependencies/dependencies';
import { DATE_TIME_WITH_SECONDS } from 'common/src/util/luxon';
import { isNonEmptyString } from 'common/src/util/string';
import { UUID } from 'common/src/util/uuid';
import { FORM_ERROR, FormApi } from 'final-form';
import { memoize } from 'lodash-es';
import { DateTime } from 'luxon';
import { parse } from 'papaparse';
import * as React from 'react';
import { FormRenderProps } from 'react-final-form';
import { CSVFormBox } from './csvFormBox';
import { ImportProcessingAlert } from './importProcessingAlert';
import { MappingsFormBoxes, StoredMappings } from './mappingsFormBoxes';
import { ImportFieldCategory } from './types';

const isFileValid = (form: FormApi<{ input: MappableImportInputWithFile }>): boolean =>
    !!form.getFieldState('input.csv.key')?.valid && !!form.getFieldState('input.csv.file')?.valid;

const removeFileFromInput = (input: MappableImportInputWithFile) => {
    const csvCopy = { ...input.csv };

    delete csvCopy.file;

    return {
        ...input,
        csv: csvCopy
    };
};

type MappableImportInputWithFile = MappableImportInput & {
    csv: {
        file?: File;
    };
};

interface CreateUpdateKey {
    slug: string;
    name: string;
}

const getAutomaticMappings = ({
    ImportInputService,
    columns,
    customFields
}: {
    ImportInputService: typeof MappableImportInputService;
    columns: string[];
    customFields: CustomFieldImportFragment[];
}): string[] =>
    columns.map((columnName) =>
        Object.keys(ImportInputService.IMPORT_COLUMNS_DEFAULT)
            .map((col) => col.toLowerCase())
            .includes(columnName.toLowerCase())
            ? (ImportInputService.IMPORT_COLUMNS_DEFAULT as any)[
                  Object.keys(ImportInputService.IMPORT_COLUMNS_DEFAULT).find(
                      (key) => key.toLowerCase() === columnName.toLowerCase()
                  )!
              ]
            : (customFields.find(
                  (cf) =>
                      cf.slug.toLowerCase() === columnName.toLowerCase() ||
                      cf.name.toLowerCase() === columnName.toLowerCase()
              )?.slug ?? null)
    );
const getAutomaticMappingsMemoized = memoize(getAutomaticMappings);

const areAllRequiredColumnsMapped = (
    ImportInputService: typeof MappableImportInputService,
    input: Pick<MappableImportInputWithFile, 'mode' | 'mappings'>
) =>
    ImportInputService.IMPORT_COLUMNS_REQUIRED(input.mode).every((column) =>
        input.mappings.includes(column)
    );

const getMissingColumnsErrorMessage = (
    ImportInputService: typeof MappableImportInputService,
    translationService: TranslationService,
    input: Pick<MappableImportInputWithFile, 'mode' | 'mappings'>
) =>
    translationService.formatListValues(
        ImportInputService.IMPORT_COLUMNS_REQUIRED(input.mode).filter(
            (column: string) => !input.mappings.includes(column)
        ),
        'conjunction'
    );

interface IMappableImportWizardInnerProps {
    ImportInputService: typeof MappableImportInputService;

    additionalCategories?: ImportFieldCategory[];
    centeredContainerRef: React.Ref<HTMLDivElement>;
    closePath: string;
    createUpdateKey?: CreateUpdateKey;
    customFields: CustomFieldImportFragment[];
    form: FormApi<{ input: MappableImportInputWithFile }>;
    importTagPrefix: string;
    isTagInputDisabled?: boolean;
    itemName?: { singular: string; plural: string };
    localStorageKeyPrefix: string;
    showModeSelection?: boolean; // TEMP
    submitting: boolean;
    title: string;
    values: { input: MappableImportInputWithFile };

    handleSubmit(): void;
}

const MappableImportWizardInner = ({
    ImportInputService,
    additionalCategories,
    centeredContainerRef,
    closePath,
    createUpdateKey,
    customFields,
    form,
    handleSubmit,
    importTagPrefix,
    isTagInputDisabled = false,
    itemName,
    localStorageKeyPrefix,
    showModeSelection = true,
    submitting,
    title,
    values
}: IMappableImportWizardInnerProps) => {
    const {
        params: { organizationId },
        translate
    } = useHeavent();
    const ids = useUniqueIds();
    const translationService = useService(TranslationService);

    const timer = React.useRef<NodeJS.Timeout | null>(null);
    const getCurrentDateTime = () => DateTime.now().toFormat(DATE_TIME_WITH_SECONDS);
    const startTagTimer = () => {
        form.change('input.tag', `${importTagPrefix}-csv-${getCurrentDateTime()}`);
        return setInterval(
            () => form.change('input.tag', `${importTagPrefix}-csv-${getCurrentDateTime()}`),
            1000
        );
    };
    const stopTagTimer = () => clearInterval(timer.current!);

    React.useEffect(() => {
        timer.current = startTagTimer();
        return () => stopTagTimer();
    }, [timer]);

    const onChange = React.useCallback(
        (file: File) => {
            form.change(
                'input.tag',
                `${importTagPrefix}-csv-${DateTime.now().toFormat(DATE_TIME_WITH_SECONDS)}`
            );

            parse<string[]>(file, {
                delimiter: values.input.csv.delimiter as CsvSettings['delimiter'],
                preview: 1,
                newline: values.input.csv.newline as CsvSettings['newline'],
                quoteChar: values.input.csv.quoteChar as CsvSettings['quoteChar'],
                complete: ({ data, errors }) => {
                    let storedMappings: StoredMappings;

                    // Errors are handled elsewhere
                    if (!errors.length) {
                        const columns = data[0];
                        const automaticMappings = getAutomaticMappingsMemoized({
                            ImportInputService,
                            columns,
                            customFields
                        });

                        try {
                            storedMappings = JSON.parse(
                                localStorage.getItem(
                                    (LocalStorageKeys as any)[`${localStorageKeyPrefix}_MAPPINGS`]
                                ) ?? '{}'
                            );
                        } catch (e) {
                            console.debug(`LocalStorage not available: ${e.toString()}`);
                        }

                        form.change(
                            'input.mappings',
                            columns.map((column, idx) =>
                                storedMappings[column] !== undefined
                                    ? storedMappings[column]
                                    : automaticMappings[idx]
                            )
                        );
                    }
                }
            });

            try {
                form.change(
                    'input.presets',
                    JSON.parse(
                        localStorage.getItem(
                            (LocalStorageKeys as any)[`${localStorageKeyPrefix}_PRESETS`](
                                organizationId
                            )
                        ) ?? '[]'
                    )
                );

                if (showModeSelection) {
                    form.change(
                        'input.mode',
                        localStorage.getItem(
                            (LocalStorageKeys as any)[`${localStorageKeyPrefix}_MODE`]
                        ) ?? ImportMode.CreateUpdate
                    );
                }
            } catch (e) {
                console.debug(`LocalStorage not available: ${e.toString()}`);
            }
        },
        [customFields, form, values]
    );

    return (
        <FullScreenPopup
            button={
                <Tooltip>
                    <Trigger>
                        <Button
                            disabled={
                                !(
                                    values.input.csv.file &&
                                    areAllRequiredColumnsMapped(ImportInputService, values.input)
                                )
                            }
                            isLoading={submitting}
                            onClick={() => {
                                stopTagTimer();
                                handleSubmit();
                            }}
                        >
                            {translate('importer_la_lis_12866')}
                        </Button>
                    </Trigger>

                    {(!values.input.csv.file ||
                        !areAllRequiredColumnsMapped(ImportInputService, values.input)) && (
                        <Content placement="top">
                            {!values.input.csv.file
                                ? translate('the_file_is_emp_77243')
                                : translate(
                                      'you_have_not_ma_46806',
                                      getMissingColumnsErrorMessage(
                                          ImportInputService,
                                          translationService,
                                          values.input
                                      )
                                  )}
                        </Content>
                    )}
                </Tooltip>
            }
            category={title}
            color="dark"
            fallbackClosePath={closePath}
            title={translate('nouvel_import_48306')}
        >
            <CenteredContainer ref={centeredContainerRef}>
                {form.getState().submitSucceeded && <ImportProcessingAlert />}
                <FormErrors errorId={ids.errorId} />

                <Flex direction="column" gap="5">
                    <CSVFormBox file={values.input.csv.file} ids={ids} onChange={onChange} />

                    {values.input.csv?.file && isFileValid(form) && (
                        <>
                            <MappingsFormBoxes<MappableImportInputWithFile>
                                additionalCategories={additionalCategories}
                                customFields={customFields}
                                defaultFields={ImportInputService.IMPORT_COLUMNS_DEFAULT}
                                file={values.input.csv.file}
                                form={form}
                                modeSelection={
                                    showModeSelection ? (
                                        <>
                                            {values.input.mode && (
                                                <Flex direction="column" gap="2">
                                                    <Box color="gray800" fontWeight="medium">
                                                        {translate('que_souhaitez_v_33936')}
                                                    </Box>
                                                    <RadioText
                                                        name="input.mode"
                                                        value={ImportMode.Create}
                                                    >
                                                        {translate(
                                                            'add_one_1_per_91911',
                                                            itemName?.singular,
                                                            createUpdateKey?.name
                                                        )}
                                                    </RadioText>
                                                    <RadioText
                                                        name="input.mode"
                                                        value={ImportMode.CreateUpdate}
                                                    >
                                                        {translate(
                                                            'add_one_1_per_73704',
                                                            itemName?.singular,
                                                            createUpdateKey?.name
                                                        )}
                                                    </RadioText>
                                                    <RadioText
                                                        name="input.mode"
                                                        value={ImportMode.Update}
                                                    >
                                                        {translate(
                                                            'do_not_add_any_50401',
                                                            itemName?.plural
                                                        )}
                                                    </RadioText>
                                                </Flex>
                                            )}
                                        </>
                                    ) : undefined
                                }
                                requiredFieldSlugs={ImportInputService.IMPORT_COLUMNS_REQUIRED(
                                    values.input.mode
                                )}
                                values={values}
                                onToggle={(isOpen) => {
                                    const file = values.input.csv.file;
                                    if (!file) {
                                        // This path shouldn't be reachable.
                                        throw new Error('No file in input!');
                                    } else {
                                        try {
                                            if (isOpen) {
                                                form.change(
                                                    'input.presets',
                                                    JSON.parse(
                                                        localStorage.getItem(
                                                            (LocalStorageKeys as any)[
                                                                `${localStorageKeyPrefix}_PRESETS`
                                                            ](organizationId)
                                                        ) ?? '[]'
                                                    )
                                                );
                                            } else {
                                                form.change('input.presets', []);
                                            }
                                        } catch (e) {
                                            console.debug(
                                                `LocalStorage not available: ${e.toString()}`
                                            );
                                        }
                                    }
                                }}
                            />

                            <FormBox
                                hideToggle={true}
                                initialIsOpen={true}
                                subtitle={translate(
                                    isTagInputDisabled
                                        ? 'your_imported_i_20879'
                                        : 'by_default_you_75833'
                                )}
                                title={translate('identify_your_i_87636')}
                            >
                                <TextInput
                                    label={translate('your_import_tag_41852')}
                                    name="input.tag"
                                    state="disabled"
                                />

                                {!isTagInputDisabled && (
                                    <>
                                        <Spacer height={2} />

                                        <TextInputComponent
                                            label={translate('specify_an_impo_41868')}
                                            onChange={(value) => {
                                                if (isNonEmptyString(value)) {
                                                    if (timer.current) {
                                                        stopTagTimer();
                                                    }
                                                    form.change(
                                                        'input.tag',
                                                        `${importTagPrefix}-csv-${value}`
                                                    );
                                                } else {
                                                    form.change(
                                                        'input.tag',
                                                        `${importTagPrefix}-csv-${getCurrentDateTime()}`
                                                    );
                                                    timer.current = startTagTimer();
                                                }
                                            }}
                                        />
                                    </>
                                )}
                            </FormBox>
                        </>
                    )}
                </Flex>
            </CenteredContainer>
        </FullScreenPopup>
    );
};

interface IMappableImportWizardProps
    extends Omit<IMappableImportWizardInnerProps, 'centeredContainerRef' | keyof FormRenderProps> {
    checkImport: (organizationId: OrganizationId, jobId: UUID) => void;
    mutate: any;
}

export const MappableImportWizard = ({
    checkImport,
    mutate,
    ...innerProps
}: IMappableImportWizardProps) => {
    const {
        params: { organizationId, eventId, delegationId },
        translate
    } = useHeavent();
    const translationService = useService(TranslationService);
    const localStorage = useLocalStorage();
    const importInputService = useService(innerProps.ImportInputService);
    const validateService = useService(ValidateService);
    const centeredContainerRef = React.useRef<HTMLDivElement | null>(null);

    const updateLocalStorage = (
        input: Pick<MappableImportInputWithFile, 'csv' | 'mappings' | 'mode' | 'presets'>
    ) => {
        const file = input.csv.file;
        if (!file) {
            // This path shouldn't be reachable.
            throw new Error('No file in input!');
        } else {
            parse<string[]>(file, {
                delimiter: input.csv.delimiter as CsvSettings['delimiter'],
                preview: 1,
                newline: input.csv.newline as CsvSettings['newline'],
                quoteChar: input.csv.quoteChar as CsvSettings['quoteChar'],
                complete: ({ data, errors }) => {
                    // Errors are handled elsewhere
                    if (!errors.length) {
                        try {
                            const columns = data[0];
                            const automaticMappings = getAutomaticMappingsMemoized({
                                ImportInputService: innerProps.ImportInputService,
                                columns,
                                customFields: innerProps.customFields
                            });
                            const storedMappings: StoredMappings = JSON.parse(
                                localStorage.getItem(
                                    (LocalStorageKeys as any)[
                                        `${innerProps.localStorageKeyPrefix}_MAPPINGS`
                                    ]
                                ) ?? '{}'
                            );
                            const newStoredMappings: StoredMappings = {};

                            for (const [index, mapping] of input.mappings.entries()) {
                                const columnName = columns[index];

                                if (mapping) {
                                    newStoredMappings[columnName] = mapping;
                                } else {
                                    delete storedMappings[columnName];
                                }

                                if (
                                    isNonEmptyString(automaticMappings[index]) &&
                                    !newStoredMappings[columnName]
                                ) {
                                    newStoredMappings[columnName] = null;
                                }
                            }

                            localStorage.setItem(
                                (LocalStorageKeys as any)[
                                    `${innerProps.localStorageKeyPrefix}_MAPPINGS`
                                ],
                                JSON.stringify({ ...storedMappings, ...newStoredMappings })
                            );
                        } catch (e) {
                            console.debug(`LocalStorage not available: ${e.toString()}`);
                        }
                    }
                }
            });

            try {
                const storedPresets = JSON.parse(
                    localStorage.getItem(
                        (LocalStorageKeys as any)[`${innerProps.localStorageKeyPrefix}_PRESETS`](
                            organizationId
                        )
                    ) ?? '[]'
                );
                localStorage.setItem(
                    (LocalStorageKeys as any)[`${innerProps.localStorageKeyPrefix}_PRESETS`](
                        organizationId
                    ),
                    JSON.stringify([
                        ...new Map(
                            [...storedPresets, ...input.presets].map((preset) => [
                                preset['customFieldId'],
                                preset
                            ])
                        ).values()
                    ])
                );

                localStorage.setItem(
                    (LocalStorageKeys as any)[`${innerProps.localStorageKeyPrefix}_FORMATS`],
                    JSON.stringify(input.csv.formats)
                );

                if (innerProps.showModeSelection) {
                    localStorage.setItem(
                        (LocalStorageKeys as any)[`${innerProps.localStorageKeyPrefix}_MODE`],
                        input.mode
                    );
                }
            } catch (e) {
                console.debug(`LocalStorage not available: ${e.toString()}`);
            }
        }
    };

    const savedImportDateTimeFormats = React.useMemo(() => {
        try {
            return JSON.parse(
                localStorage.getItem(
                    (LocalStorageKeys as any)[`${innerProps.localStorageKeyPrefix}_FORMATS`]
                ) ?? '{}'
            );
        } catch {
            return null;
        }
    }, []);

    return (
        <Form
            height={1}
            hideErrors={true}
            initialValues={{
                input: importInputService.default(savedImportDateTimeFormats)
                    .input as MappableImportInputWithFile
            }}
            mutators={{
                setValue: ([field, value], state, { changeValue }) =>
                    changeValue(state, field, () => value)
            }}
            render={({ form, handleSubmit, submitting, values }) => (
                <MappableImportWizardInner
                    centeredContainerRef={centeredContainerRef}
                    form={form}
                    handleSubmit={handleSubmit}
                    submitting={submitting}
                    values={values}
                    {...innerProps}
                />
            )}
            validate={validateService.validateForForm(importInputService.schema())}
            onShowErrors={() => {
                if (centeredContainerRef.current) {
                    centeredContainerRef.current.scrollTop = 0;
                }
            }}
            // @ts-expect-error Happy path does not require a return value
            onSubmit={async ({ input }) => {
                if (!areAllRequiredColumnsMapped(innerProps.ImportInputService, input)) {
                    return {
                        [FORM_ERROR]: translate(
                            'you_have_not_ma_46806',
                            getMissingColumnsErrorMessage(
                                innerProps.ImportInputService,
                                translationService,
                                input
                            )
                        )
                    };
                }

                updateLocalStorage(input);

                const inputWithoutFile = removeFileFromInput(input);

                try {
                    const { jobId } = await mutate({
                        organizationId,
                        eventId,
                        delegationId,
                        input: inputWithoutFile
                    });
                    if (centeredContainerRef.current) {
                        centeredContainerRef.current.scrollTop = 0;
                    }
                    checkImport(organizationId, jobId);
                } catch {
                    /* Already displayed in <FormErrors />. */
                }
            }}
        />
    );
};
